import { useMemo, useState } from 'react';
import { formatCents } from '@pirouette/shared';
import {
  usePaymentPlans,
  usePaymentPlanInstallments,
  useCreatePaymentPlan,
  type PaymentPlanRow,
  type PaymentPlanInstallmentRow,
} from '@/data/payments';
import {
  installmentTone,
  planTone,
  planProgress,
  installmentRetryDetail,
} from '@/components/billing/paymentPlanView';
import { FinancialMfaNotice } from '@/components/admin/FinancialMfaNotice';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { toast } from '@/lib/toast';

type Cadence = 'monthly' | 'biweekly' | 'weekly';

interface AdminPaymentPlansSectionProps {
  familyId: string;
  /** Whether the admin's MFA check is current. */
  mfaCurrent: boolean;
  /** Whether the MFA state is still loading (suppresses the notice while loading). */
  mfaLoading: boolean;
}

/**
 * Admin "Payment plans" surface on the family billing detail. A create panel —
 * gated behind the same admin-MFA notice as refunds — plus the family's
 * existing plans with per-installment status badges and a paid/total progress
 * summary. Uses the inline expanding-panel idiom (no Dialog primitive exists).
 */
export function AdminPaymentPlansSection({
  familyId,
  mfaCurrent,
  mfaLoading,
}: AdminPaymentPlansSectionProps) {
  const plans = usePaymentPlans(familyId);
  const createPlan = useCreatePaymentPlan();

  const [showCreate, setShowCreate] = useState(false);
  const [total, setTotal] = useState('');
  const [installmentCount, setInstallmentCount] = useState('3');
  const [startDate, setStartDate] = useState('');
  const [cadence, setCadence] = useState<Cadence>('monthly');
  const [notes, setNotes] = useState('');

  function resetForm() {
    setTotal('');
    setInstallmentCount('3');
    setStartDate('');
    setCadence('monthly');
    setNotes('');
  }

  async function handleCreate() {
    if (!mfaCurrent) {
      toast({
        title: 'Admin MFA check required',
        description:
          'Open Security to send and verify an email code before creating payment plans.',
        variant: 'error',
      });
      return;
    }

    const totalCents = Math.round(parseFloat(total) * 100);
    if (!Number.isFinite(totalCents) || totalCents <= 0) {
      toast({ title: 'Enter a valid plan total', variant: 'error' });
      return;
    }
    const count = Number.parseInt(installmentCount, 10);
    if (!Number.isInteger(count) || count < 1) {
      toast({ title: 'Enter at least one installment', variant: 'error' });
      return;
    }
    if (!startDate) {
      toast({ title: 'Pick a start date', variant: 'error' });
      return;
    }

    try {
      const { installments } = await createPlan.mutateAsync({
        family_id: familyId,
        total_cents: totalCents,
        installment_count: count,
        start_date: startDate,
        cadence,
        notes: notes.trim() || undefined,
      });
      setShowCreate(false);
      resetForm();
      toast({
        title: 'Payment plan created',
        description: `${installments.length} installment${installments.length === 1 ? '' : 's'} scheduled.`,
        variant: 'success',
      });
    } catch (err) {
      toast({
        title: "Couldn't create payment plan",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  const planList = plans.data ?? [];

  return (
    <section aria-labelledby="payment-plans-heading">
      <div className="flex items-baseline justify-between">
        <h2 id="payment-plans-heading" className="text-[var(--text-xl)]">
          Payment plans
        </h2>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setShowCreate((v) => !v)}
          aria-expanded={showCreate}
        >
          {showCreate ? 'Cancel' : 'Create payment plan'}
        </Button>
      </div>

      {showCreate && (
        <div className="surface-elevated mt-4 grid gap-4 p-5">
          {!mfaLoading && !mfaCurrent && <FinancialMfaNotice />}

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label htmlFor="plan-total">Total</Label>
              <Input
                id="plan-total"
                type="number"
                step="0.01"
                min="0"
                value={total}
                onChange={(e) => setTotal(e.target.value)}
                placeholder="900.00"
              />
            </div>
            <div>
              <Label htmlFor="plan-count">Installments</Label>
              <Input
                id="plan-count"
                type="number"
                step="1"
                min="1"
                value={installmentCount}
                onChange={(e) => setInstallmentCount(e.target.value)}
                placeholder="3"
              />
            </div>
            <div>
              <Label htmlFor="plan-start">Start date</Label>
              <Input
                id="plan-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="plan-cadence">Cadence</Label>
              <select
                id="plan-cadence"
                className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
                value={cadence}
                onChange={(e) => setCadence(e.target.value as Cadence)}
              >
                <option value="monthly">Monthly</option>
                <option value="biweekly">Biweekly</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
          </div>

          <div>
            <Label htmlFor="plan-notes">Notes (optional)</Label>
            <Input
              id="plan-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. recital season tuition split"
            />
          </div>

          <p className="text-[var(--color-ink-subtle)] text-[var(--text-xs)]">
            The schedule is generated from the start date and cadence. Installments split the total
            across the chosen number of payments.
          </p>

          <Button
            onClick={() => void handleCreate()}
            disabled={!total || !startDate || createPlan.isPending || mfaCurrent === false}
          >
            {createPlan.isPending ? 'Creating…' : 'Create payment plan'}
          </Button>
        </div>
      )}

      {plans.isError && (
        <p className="mt-4 text-[var(--color-danger)] text-[var(--text-sm)]">
          Couldn't load payment plans.
        </p>
      )}
      {!plans.isError && planList.length === 0 && (
        <p className="mt-4 text-[var(--color-ink-muted)]">No payment plans yet.</p>
      )}

      {planList.length > 0 && (
        <ul className="mt-4 grid gap-3">
          {planList.map((plan) => (
            <AdminPlanCard key={plan.id} plan={plan} />
          ))}
        </ul>
      )}
    </section>
  );
}

function AdminPlanCard({ plan }: { plan: PaymentPlanRow }) {
  const installmentsQuery = usePaymentPlanInstallments(plan.id);
  // Memoized: react-hooks/exhaustive-deps is an ERROR here, and these derive
  // fresh arrays/objects each render otherwise.
  const installments = useMemo(() => installmentsQuery.data ?? [], [installmentsQuery.data]);
  const progress = useMemo(() => planProgress(installments), [installments]);

  return (
    <li className="surface p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <p className="font-medium tabular-nums">
            {formatCents(plan.total_cents)}{' '}
            <span className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
              · {plan.installment_count} installment{plan.installment_count === 1 ? '' : 's'}
            </span>
          </p>
          {plan.notes && (
            <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">{plan.notes}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[var(--color-ink-muted)] text-[var(--text-sm)] tabular-nums">
            {formatCents(progress.paidCents)} / {formatCents(progress.totalCents)} paid
            {progress.totalCount > 0 && ` · ${progress.paidCount}/${progress.totalCount}`}
          </span>
          <Badge tone={planTone(plan.status)}>{plan.status}</Badge>
        </div>
      </div>

      {installmentsQuery.isError && (
        <p className="mt-3 text-[var(--color-danger)] text-[var(--text-sm)]">
          Couldn't load this plan's installments.
        </p>
      )}

      {installments.length > 0 && (
        <ul className="mt-3 grid gap-2 border-t border-[var(--color-border)] pt-3">
          {installments.map((inst, idx) => (
            <AdminInstallmentRow key={inst.id} installment={inst} index={idx} />
          ))}
        </ul>
      )}
    </li>
  );
}

function AdminInstallmentRow({
  installment,
  index,
}: {
  installment: PaymentPlanInstallmentRow;
  index: number;
}) {
  const retry = useMemo(() => installmentRetryDetail(installment), [installment]);
  const isFailed = installment.status === 'failed';
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 text-[var(--text-sm)]">
      <div>
        <span className="text-[var(--color-ink-muted)]">#{index + 1}</span>{' '}
        <span className="tabular-nums">{installment.due_date}</span>
        {isFailed && retry.lastError && (
          <span className="ml-2 text-[var(--color-danger)] text-[var(--text-xs)]">
            {retry.lastError}
            {retry.attempts != null &&
              ` (${retry.attempts} attempt${retry.attempts === 1 ? '' : 's'})`}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className="tabular-nums">{formatCents(installment.amount_cents)}</span>
        <Badge tone={installmentTone(installment.status)}>{installment.status}</Badge>
      </div>
    </li>
  );
}
